<script setup lang="ts">
/*
 *
 * IMPORTS
 *
 * */

// PRIMEVUE
import { default as PInputGroup } from 'primevue/inputgroup'
import { default as PTextarea } from 'primevue/textarea'
import { default as PInputGroupAddon } from 'primevue/inputgroupaddon'
import { default as PButton } from 'primevue/button'

// Custom components and types
import { QuizQuestionAnswerMatching } from '@/core/domain/quiz/QuizQuestionAnswer.ts'
import { LearningQuizQuestionType } from '@/core/domain/resources/enums/LearningQuizQuestion.ts'

// VUE, VUE ROUTER, ETC...
import { type ModelRef, computed } from 'vue'

const answers: ModelRef<QuizQuestionAnswerMatching[]> = defineModel('answers', {
  required: true
})

const props = defineProps<{
  canModifyStructure?: boolean
  canModifyContent?: boolean
}>()

/**
 * Ajoute une nouvelle paire de correspondance à la liste
 */
function addQuestion() {
  answers.value.push(
    QuizQuestionAnswerMatching.create({
      type: LearningQuizQuestionType.Values.MATCHING,
      left: {
        type: LearningQuizQuestionType.Values.MATCHING,
        value: ''
      },
      right: {
        type: LearningQuizQuestionType.Values.MATCHING,
        value: ''
      }
    })
  )
}

/**
 * Convertit un nombre en lettre (0 -> a, 1 -> b, etc.)
 */
function getLetterFromNumber(num: number) {
  const a_CODE = 97 // Code ASCII pour 'a'
  return String.fromCharCode(a_CODE + num)
}

/**
 * Supprime une paire de correspondance
 */
function deleteAnswer(index: number) {
  answers.value.splice(index, 1)
}

/**
 * Vérifie si toutes les paires ont des valeurs non vides
 */
const hasEmptyFields = computed(() => {
  return answers.value.some((answer) => !answer.left.value.trim() || !answer.right.value.trim())
})

/**
 * Réinitialise toutes les paires
 */
function resetAllAnswers() {
  if (confirm('Êtes-vous sûr de vouloir réinitialiser toutes les paires ?')) {
    answers.value = []
    addQuestion() // Ajouter une paire vide pour commencer
  }
}
</script>

<template>
  <div class="flex flex-col gap-1">
    <div v-for="(answer, i) in answers" :key="`answer_${i}`" class="grid grid-cols-12 gap-4">
      <div class="col-span-12 flex items-center gap-1">
        <p>Paire {{ i + 1 }}</p>
        <p-button
          icon="pi pi-trash"
          size="small"
          outlined
          text
          class="p-1"
          severity="secondary"
          :disabled="!props.canModifyStructure"
          aria-label="Supprimer cette paire"
          @click="() => deleteAnswer(i)"
        />
      </div>
      <div class="col-span-6">
        <p-input-group>
          <p-input-group-addon>
            <label>{{ getLetterFromNumber(i) }}</label>
          </p-input-group-addon>
          <p-textarea
            v-model="answer.left.value"
            class="flex-1"
            placeholder="Texte de la réponse à gauche"
            :disabled="!props.canModifyContent"
            aria-label="Élément de gauche de la paire"
          />
        </p-input-group>
      </div>

      <div class="col-span-6">
        <p-input-group>
          <p-input-group-addon>
            <label>{{ i + 1 }}</label>
          </p-input-group-addon>
          <p-textarea
            v-model="answer.right.value"
            class="flex-1"
            placeholder="Texte de la réponse à droite"
            :disabled="!props.canModifyContent"
            aria-label="Élément de droite de la paire"
          />
        </p-input-group>
      </div>
    </div>

    <div class="mt-2 text-sm text-red-500" v-if="hasEmptyFields">
      Attention : certains champs sont vides. Toutes les paires doivent être complétées.
    </div>

    <!-- Messages d'information sur les contraintes -->
    <div
      v-if="!props.canModifyStructure && props.canModifyContent"
      class="mt-2 text-xs text-amber-600"
    >
      <i class="pi pi-info-circle mr-1"></i>
      Impossible d'ajouter/supprimer des paires sur cette version
    </div>

    <div v-if="!props.canModifyContent" class="mt-2 text-xs text-red-600">
      <i class="pi pi-lock mr-1"></i>
      Version verrouillée - aucune modification autorisée
    </div>
  </div>

  <div class="mt-4 flex gap-2">
    <p-button
      label="Ajouter une paire"
      icon="pi pi-plus"
      size="small"
      outlined
      :disabled="!props.canModifyStructure"
      aria-label="Ajouter une nouvelle paire"
      @click="addQuestion"
    />

    <p-button
      v-if="answers.length > 0"
      label="Réinitialiser"
      icon="pi pi-refresh"
      size="small"
      severity="secondary"
      outlined
      :disabled="!props.canModifyStructure"
      aria-label="Réinitialiser toutes les paires"
      @click="resetAllAnswers"
    />
  </div>
</template>
